import { ReactElement, useContext, useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { Button, Loading } from "@wfp/ui"

import { DataUploadContext } from "../../providers/DataUploadContext"
import useCommon from "../../hooks/useCommon"
import { useLazyGetMSByIdQuery } from "../../services/marketprices/mappingSchemas"

import { excelMappingSteps } from "../../constants/markets"
import { MappingSchemaStatus } from "../../constants/prices"
import { ExcelMappingSteps } from "../../interfaces/markets"

import ProgressStepper from "../../components/Shared/ProgressStepper/ProgressStepper"
import MappingStepWrapper from "../../components/Markets/ExcelDataUpload/Mapping/MappingStepWrapper"

const MappingSchemaPage = (): ReactElement => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { showSnackBar } = useCommon()

  const {
    filters,
    setFilters,
    completedStep,
    setCompletedStep,
    isLoading,
    handleNewMapping,
    newMapping,
    setNewMapping,
  } = useContext(DataUploadContext)
  const prevFiltersRef = useRef(filters)

  const [activeStep, setActiveStep] = useState<number>(id ? ExcelMappingSteps.HEADER : ExcelMappingSteps.DATA)
  const [getMSData] = useLazyGetMSByIdQuery()

  const validateStep = (): boolean => {
    switch (activeStep) {
      case ExcelMappingSteps.DATA:
        return !!filters.file && !!filters.adm0Code && !!filters.mappingSchemaName
      case ExcelMappingSteps.GLOBAL:
        return !!newMapping?.isCompleted
      default:
        return true
    }
  }

  const handleBack = (): void => {
    if (activeStep === ExcelMappingSteps.HEADER && id) {
      return navigate("/market-prices/MappingSchema")
    }

    if (activeStep > ExcelMappingSteps.DATA) {
      return setActiveStep(prev => prev - 1)
    }

    navigate("/market-prices/MappingSchema")
  }

  const handleNext = async (): Promise<void> => {
    if (activeStep <= excelMappingSteps.length) {
      if (activeStep > completedStep || JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters)) {
        try {
          switch (activeStep) {
            case ExcelMappingSteps.DATA:
              await handleNewMapping()
              break

            default:
              break
          }
        } catch (error) {
          await showSnackBar(error?.toString() ?? "An error occurred while processing your request", "error")
          return
        }

        setCompletedStep(activeStep)
        prevFiltersRef.current = { ...filters }
      }

      if (activeStep < excelMappingSteps.length) {
        setActiveStep(prev => prev + 1)
      } else {
        navigate("/market-prices/MappingSchema")
      }
    }
  }

  const renderContent = (): ReactElement | null => {
    const content = excelMappingSteps.find(item => item.id === activeStep)?.content ?? null

    if (content && activeStep > ExcelMappingSteps.DATA) {
      return <MappingStepWrapper mappingSchemaId={Number(id) || newMapping?.id || 0}>{content}</MappingStepWrapper>
    }

    return content
  }

  useEffect(() => {
    ;(async () => {
      if (id) {
        const msData = await getMSData(Number(id)).unwrap()

        setFilters(prev => ({ ...prev, mappingSchemaId: Number(id) }))
        setNewMapping({
          id: Number(id),
          name: msData?.mappingSchemaName ?? "",
          mappingSchemaJSON: msData?.mappingSchemaJSON ?? [],
          isCompleted: msData?.mappingSchemaStatusId === MappingSchemaStatus.Complete,
        })
      }
    })()
  }, [id])

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 125px)" }}>
      <div className="w-full border-b-2 border-solid border-gray-200 pb-1 pt-2 bg-white z-30 flex-shrink-0">
        <ProgressStepper steps={excelMappingSteps} currentStep={activeStep} />
      </div>

      <div className="flex-1 overflow-y-auto pt-8 px-6">
        {isLoading ? (
          <div className="min-h-[400px] w-full flex justify-center items-center">
            <Loading small withOverlay={false} />
          </div>
        ) : (
          renderContent()
        )}
      </div>

      <div className="flex justify-between w-full border-t-2 border-solid border-gray-200 pt-4 pb-4 bg-white px-6 flex-shrink-0">
        <Button kind="secondary" onClick={handleBack}>
          {activeStep > ExcelMappingSteps.DATA ? "Back" : "Cancel"}
        </Button>

        <Button onClick={handleNext} disabled={isLoading || !validateStep()}>
          {activeStep < ExcelMappingSteps.GLOBAL ? "Next" : "Complete"}
        </Button>
      </div>
    </div>
  )
}

export default MappingSchemaPage
